import {
  useEffect,
  useState,
  useRef,
  ChangeEvent,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { useDebounce } from 'hooks';
import {
  InvestorSpaceModel,
  State,
  SortParams,
  SortParamsArg,
  InvestorSpaceFilters,
  Option,
  InvestorSpaceOptionsModel,
  PagingResponseModel,
} from 'models';
import {
  size as _size,
  map as _map,
  join as _join,
  get as _get,
  concat as _concat,
} from 'lodash';

import { getCurrentCooperative } from 'store/home/selectors';
import services from 'services';

import { InvestorSpaceGrid } from 'components';

interface FetchOptions {
  page?: {
    number: number;
    size: number;
  };
  sortParams: SortParams;
  filters: InvestorSpaceFilters;
}

function buildSearchParams(options: FetchOptions) {
  let searchParams = '';

  if (options.page) {
    searchParams += `requestSettings.pageNumber=${options.page.number}&`;
    searchParams += `requestSettings.pageCount=${options.page.size}&`;
  }

  if (options.sortParams.orderBy === 'default') {
    searchParams += `requestSettings.sort=uds_cooperativeid:asc&requestSettings.sort=uds_spacetypecode:asc&requestSettings.sort=uds_name:asc`;
  } else {
    searchParams += `requestSettings.sort=${options.sortParams.orderBy}:${options.sortParams.order}`;
  }

  if (options.filters.uds_cooperativeid) {
    searchParams += `&filterSettings.filters=uds_cooperativeid|account|name:ref:${options.filters.uds_cooperativeid}`;
  }

  if (options.filters.uds_accessaddress) {
    searchParams += `&filterSettings.filters=uds_accessaddress:str:${options.filters.uds_accessaddress}`;
  }

  if (options.filters.uds_spaceaddresscity) {
    searchParams += `&filterSettings.filters=uds_spaceaddresscity:str:${options.filters.uds_spaceaddresscity}`;
  }

  if (options.filters.uds_name) {
    searchParams += `&filterSettings.filters=uds_name:str:${options.filters.uds_name}`;
  }

  if (
    options.filters.uds_squaremeters_min ||
    options.filters.uds_squaremeters_max
  ) {
    const { uds_squaremeters_min: min, uds_squaremeters_max: max } =
      options.filters;

    searchParams += `&filterSettings.filters=uds_squaremeters:rng:${min}|${max}`;
  }

  if (_size(options.filters.uds_spacestatuscode)) {
    let statuses = _join(
      _map(options.filters.uds_spacestatuscode, (status) => status.Value),
      '|'
    );

    searchParams += `&filterSettings.filters=uds_spacestatuscode:opt:${statuses}`;
  }

  if (_size(options.filters.uds_spacetypecode)) {
    let types = _join(
      _map(options.filters.uds_spacetypecode, (type) => type.Value),
      '|'
    );

    searchParams += `&filterSettings.filters=uds_spacetypecode:opt:${types}`;
  }

  return searchParams;
}

interface InvestorSpaceState {
  spacesList: InvestorSpaceModel[];
  sortParams: SortParams;
  loading: boolean;
  error: boolean;
  refresh: boolean;
  filters: InvestorSpaceFilters;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  statusesOptions: Option[];
  typesOptions: Option[];
}

const InvestorSpace = () => {
  const fetched = useRef<null | boolean>(null);
  const { coopId, coopName } = useSelector((state: State) => ({
    coopId: state.settings.current_cooperative,
    coopName: getCurrentCooperative(state)?.Name,
  }));
  const [state, setState] = useState<InvestorSpaceState>({
    spacesList: [],
    loading: false,
    error: false,
    refresh: false,
    sortParams: {
      order: 'asc',
      orderBy: 'default',
      type: 'string',
    },
    pageNumber: 1,
    pageSize: 15,
    totalCount: 0,
    statusesOptions: [],
    typesOptions: [],
    filters: {
      uds_accessaddress: '',
      uds_cooperativeid: '',
      uds_name: '',
      uds_spaceaddresscity: '',
      uds_spacestatuscode: [],
      uds_spacetypecode: [],
      uds_squaremeters_max: '',
      uds_squaremeters_min: '',
    },
  });

  const debouncedFilters = useDebounce(state.filters, 700);
  const debouncedSortParams = useDebounce(state.sortParams, 400);
  const debouncedPageNumber = useDebounce(state.pageNumber, 700);

  const downloadLink = useMemo(() => {
    const searchParams = buildSearchParams({
      sortParams: state.sortParams,
      filters: state.filters,
    });

    return `/api/portfolios/${coopId}/spaces/download?${searchParams}`;
  }, [coopId, state.filters, state.sortParams]);

  const fetchOptions = async (coopId: string) => {
    const investorSpaceOptions: InvestorSpaceOptionsModel = (
      await services.get(`/api/portfolios/${coopId}/spaces/options`)
    ).data.Data;

    return investorSpaceOptions;
  };

  const fetchSpacesRes = async (coopId: string, options: FetchOptions) => {
    const searchParams = buildSearchParams(options);

    const spacesRes: {
      Data: InvestorSpaceModel[];
      Paging: PagingResponseModel;
    } = (await services.get(`/api/portfolios/${coopId}/spaces?${searchParams}`))
      .data;

    return spacesRes;
  };

  useEffect(() => {
    if (coopId) {
      (async function () {
        try {
          setState((prevState) => ({
            ...prevState,
            loading: true,
            error: false,
          }));

          const spacesOptions = await fetchOptions(coopId);
          const { Data, Paging } = await fetchSpacesRes(coopId, {
            page: {
              number: state.pageNumber,
              size: state.pageSize,
            },
            sortParams: state.sortParams,
            filters: state.filters,
          });

          setState((prevState) => ({
            ...prevState,
            spacesList: Data,
            totalCount: Paging.TotalCount || 0,
            statusesOptions: spacesOptions.SpaceStatusOptions,
            typesOptions: spacesOptions.SpaceTypeOptions,
            loading: false,
          }));

          fetched.current = true;
        } catch (err) {
          console.error(err);

          setState((prevState) => ({
            ...prevState,
            loading: false,
            error: true,
          }));
        }
      })();
    }
  }, [coopId]);

  useEffect(() => {
    if (fetched.current && coopId) {
      (async function () {
        try {
          setState((prevState) => ({ ...prevState, refresh: true }));

          const { Data, Paging } = await fetchSpacesRes(coopId, {
            page: {
              number: debouncedPageNumber,
              size: state.pageSize,
            },
            sortParams: debouncedSortParams,
            filters: debouncedFilters,
          });

          setState((prevState) => ({
            ...prevState,
            spacesList: Data,
            totalCount: Paging.TotalCount || 0,
            refresh: false,
          }));
        } catch (err) {
          setState((prevState) => ({
            ...prevState,
            refresh: false,
            error: true,
          }));
        }
      })();
    }

    /* eslint-disable-next-line */
  }, [
    debouncedPageNumber,
    state.pageSize,
    debouncedSortParams,
    debouncedFilters,
  ]);

  const handleChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setState((prevState) => ({
      ...prevState,
      pageNumber: 1,
      filters: {
        ...prevState.filters,
        [name]: value,
      },
    }));
  }, []);

  const handleChangeSortParams = useCallback((params: SortParamsArg) => {
    setState((prevState) => ({
      ...prevState,
      sortParams: {
        order: prevState.sortParams.order === 'asc' ? 'desc' : 'asc',
        orderBy: params.id,
        type: params.type,
      },
    }));
  }, []);

  const handleChangeMultiSelect = useCallback(
    (option: Option, name?: string) => {
      if (name) {
        setState((prevState) => {
          let list: Option[] = _get(prevState.filters, name);

          const isItemExist = list.some((item) => item.Value === option.Value);
          const newList = isItemExist
            ? list.filter((item) => item.Value !== option.Value)
            : _concat(list, option);

          return {
            ...prevState,
            pageNumber: 1,
            filters: {
              ...prevState.filters,
              [name]: newList,
            },
          };
        });
      }
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      sortParams: {
        order: 'asc',
        orderBy: 'default',
        type: 'string',
      },
      pageNumber: 1,
      pageSize: 15,
      filters: {
        uds_accessaddress: '',
        uds_cooperativeid: '',
        uds_name: '',
        uds_spaceaddresscity: '',
        uds_spacestatuscode: [],
        uds_spacetypecode: [],
        uds_squaremeters_max: '',
        uds_squaremeters_min: '',
      },
    }));
  }, []);

  const handleChangeCurrentPage = useCallback(
    (e: ChangeEvent<unknown>, page: number) => {
      setState((prevState) => ({
        ...prevState,
        pageNumber: page,
      }));
    },
    []
  );

  return (
    <InvestorSpaceGrid
      {...state}
      coopName={coopName}
      downloadLink={downloadLink}
      handleChangeCurrentPage={handleChangeCurrentPage}
      handleChangeInput={handleChangeInput}
      handleResetFilters={handleResetFilters}
      handleChangeSortParams={handleChangeSortParams}
      handleChangeMultiSelect={handleChangeMultiSelect}
    />
  );
};

export default InvestorSpace;
